import basicExample from '../../../../asset/images/floor-plans/basic-example.webp';
import basicCloseUp from '../../../../asset/images/floor-plans/basic-close-up.webp';
import premiumExample from '../../../../asset/images/floor-plans/premium-example.webp';
import premiumCloseUp from '../../../../asset/images/floor-plans/premium-close-up.webp';
import premiumFloor1 from '../../../../asset/images/floor-plans/premium-floor-1.webp';
import premiumFloor2 from '../../../../asset/images/floor-plans/premium-floor-2.webp';
import premiumAllFloors from '../../../../asset/images/floor-plans/premium-all-floors.webp';
import threeDExample from '../../../../asset/images/floor-plans/3d-example.webp';
import threeDFloor1 from '../../../../asset/images/floor-plans/3d-floor-1.webp';
import threeDFloor2 from '../../../../asset/images/floor-plans/3d-floor-2.webp';

import type { FloorPlanTierId } from './floorPlanOffer';

/**
 * DV-3204 BAND-AID — what each tier card and its example dialog show, taken
 * from the Claude Design project (floor-plan-tiers). The catalog has one image
 * and one description per service, and the design needs feature lists, a
 * close-up and per-floor example files, so all of it lives here and the images
 * are bundled. Proper fix: the content served from the catalog and the images
 * from the CDN (tracked in docs/engineering-backlog.md, DV-3204).
 *
 * The card image itself already comes from the catalog (DV-3808): see
 * `FloorPlanTierImage`. `exampleImage` is only its fallback, and the example
 * dialog's own image, which belongs with the close-up and files beside it.
 */

export interface FloorPlanFeature {
  label: string;
  included: boolean;
}

export interface FloorPlanExampleFile {
  src: string;
  label: string;
}

export interface FloorPlanFileGroup {
  /** Shown only when a tier has more than one group. */
  title?: string;
  files: FloorPlanExampleFile[];
}

export interface FloorPlanTierContent {
  /** "Everything in Basic" etc.; the tier below's features carry up. */
  inherits?: string;
  features: FloorPlanFeature[];
  exampleImage: string;
  closeUpImage?: string;
  filesHeading?: string;
  fileGroups: FloorPlanFileGroup[];
}

const PREMIUM_FILES: FloorPlanExampleFile[] = [
  { src: premiumFloor1, label: '1st Floor' },
  { src: premiumFloor2, label: '2nd Floor' },
  { src: premiumAllFloors, label: 'All Floors Combined' }
];

export const FLOOR_PLAN_TIER_CONTENT: Record<
  FloorPlanTierId,
  FloorPlanTierContent
> = {
  basic: {
    features: [
      { label: 'Room labels & dimensions', included: true },
      { label: 'Color-coded rooms', included: false },
      { label: 'Fixtures & appliances', included: false },
      { label: 'Door swings', included: false }
    ],
    exampleImage: basicExample,
    closeUpImage: basicCloseUp,
    fileGroups: []
  },
  premium: {
    inherits: 'Everything in Basic',
    features: [
      { label: 'Color-coded rooms', included: true },
      { label: 'Fixtures & appliances', included: true },
      { label: 'Door swings', included: true }
    ],
    exampleImage: premiumExample,
    closeUpImage: premiumCloseUp,
    filesHeading: "Files You'll Receive",
    fileGroups: [{ files: PREMIUM_FILES }]
  },
  '3d': {
    inherits: 'Everything in Premium',
    features: [
      { label: 'Furnished 3D view', included: true },
      { label: 'Colors and textures', included: true }
    ],
    exampleImage: threeDExample,
    filesHeading: "Everything You'll Receive",
    fileGroups: [
      {
        title: '3D Floor Plan Renders',
        files: [
          { src: threeDFloor1, label: '1st Floor' },
          { src: threeDFloor2, label: '2nd Floor' }
        ]
      },
      { title: 'Premium Floor Plans', files: PREMIUM_FILES }
    ]
  }
};
